import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from '../components/Toast';
import { timeAgo } from '../lib/constants';

export default function NewRequestsPage({ desktop, room, session, onOpenClaim, onBack }) {
  const [rows, setRows] = useState(null); // null = loading; [{ claim, request }]
  const [photoUrls, setPhotoUrls] = useState({}); // request.id -> [signedUrl]
  const [busy, setBusy] = useState(null); // claim.id currently being acted on

  const load = useCallback(async () => {
    const [{ data: claims }, { data: reqs }] = await Promise.all([
      supabase.from('claims').select('*').eq('room_id', room.id).eq('unreviewed', true).order('created_at', { ascending: false }),
      supabase.from('intake_requests').select('*').eq('room_id', room.id).eq('status', 'converted'),
    ]);
    const byClaim = {};
    (reqs || []).forEach(r => { if (r.claim_id) byClaim[r.claim_id] = r; });
    const merged = (claims || []).map(c => ({ claim: c, request: byClaim[c.id] || null }));
    setRows(merged);

    const urls = {};
    for (const { request } of merged) {
      if (!request || !request.photo_paths || request.photo_paths.length === 0) continue;
      const { data: signed } = await supabase.storage.from('claim-files').createSignedUrls(request.photo_paths, 3600);
      urls[request.id] = (signed || []).filter(s => s.signedUrl).map(s => s.signedUrl);
    }
    setPhotoUrls(urls);
  }, [room.id]);

  useEffect(() => { load(); }, [load]);

  async function markReviewed({ claim, request }) {
    setBusy(claim.id);
    // Move intake photos into the claim's folder so they show up in the Files tab
    if (request && request.photo_paths && request.photo_paths.length > 0) {
      const newPaths = [];
      for (const path of request.photo_paths) {
        const fileName = path.split('/').pop();
        const dest = `${room.id}/${claim.id}/${fileName}`;
        const { error } = await supabase.storage.from('claim-files').move(path, dest);
        newPaths.push(error ? path : dest);
      }
      await supabase.from('intake_requests').update({ photo_paths: newPaths }).eq('id', request.id);
    }
    const { error } = await supabase.from('claims').update({ unreviewed: false }).eq('id', claim.id);
    setBusy(null);
    if (error) { toast('Could not mark as reviewed', 'error'); return; }
    toast('Marked as reviewed', 'success');
    load();
  }

  async function dismissDelete({ claim, request }) {
    if (!window.confirm(`Delete this request and its claim (${claim.car || claim.client_name || 'no details'})? This can't be undone.`)) return;
    setBusy(claim.id);
    if (request && request.photo_paths && request.photo_paths.length > 0) {
      await supabase.storage.from('claim-files').remove(request.photo_paths);
    }
    if (request) await supabase.from('intake_requests').update({ status: 'dismissed', photo_paths: [] }).eq('id', request.id);
    const { error } = await supabase.from('claims').delete().eq('id', claim.id);
    setBusy(null);
    if (error) { toast('Could not delete claim — admins only', 'error'); return; }
    toast('Request dismissed', 'success');
    load();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header */}
      <div style={{ height: 64, padding: '0 18px', boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: desktop ? 22 : 18, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' }}>
          New Requests{rows && rows.length > 0 && <span style={{ color: 'var(--orange)' }}> ({rows.length})</span>}
        </div>
        <button onClick={onBack} className="raise-3d" style={{ marginLeft: 'auto', background: 'var(--bg3)', border: 'none', color: 'var(--text2)', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
        </button>
      </div>

      {/* Body */}
      <div className="modal-scroll" style={{ flex: 1, padding: desktop ? 18 : 14 }}>
        {!rows ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" style={{ width: 26, height: 26 }} /></div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text3)' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text2)' }}>No new requests</div>
            <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>Client submissions from your intake link will show up here.</div>
          </div>
        ) : rows.map(({ claim, request }) => (
          <div key={claim.id} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 13, padding: desktop ? 18 : 14, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: desktop ? 21 : 18, fontWeight: 700 }}>{claim.car || 'Unknown vehicle'}</span>
              {claim.plate && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text3)', fontWeight: 600 }}>{claim.plate}</span>}
              <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--text3)' }}>{timeAgo(claim.created_at)}</span>
            </div>
            <div style={{ fontSize: 13.5, color: 'var(--text2)', marginTop: 6 }}>
              {claim.client_name}
              {claim.client_phone && <> · <a href={`tel:${claim.client_phone}`} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{claim.client_phone}</a></>}
              {request && request.client_email && <> · {request.client_email}</>}
            </div>
            {(claim.insurer || claim.claim_num) && (
              <div style={{ fontSize: 12.5, color: 'var(--text3)', marginTop: 4 }}>
                {claim.insurer}{claim.claim_num && <> · Claim # <span style={{ fontFamily: 'var(--font-mono)' }}>{claim.claim_num}</span></>}
              </div>
            )}
            {claim.notes && (
              <div style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--text)', marginTop: 10, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', whiteSpace: 'pre-wrap' }}>{claim.notes}</div>
            )}
            {request && photoUrls[request.id] && photoUrls[request.id].length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                {photoUrls[request.id].map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noreferrer" style={{ width: 72, height: 72, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', flexShrink: 0 }}>
                    <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </a>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              <button className="btn btn-secondary" style={{ flex: 1, minWidth: 130 }} onClick={() => onOpenClaim(claim.id)}>Open Full Claim</button>
              <button className="btn btn-primary" style={{ flex: 1, minWidth: 130 }} disabled={busy === claim.id} onClick={() => markReviewed({ claim, request })}>
                {busy === claim.id ? <div className="spinner" /> : 'Mark Reviewed'}
              </button>
              {room.role === 'admin' && (
                <button className="btn btn-danger" style={{ flexBasis: '100%' }} disabled={busy === claim.id} onClick={() => dismissDelete({ claim, request })}>Dismiss & Delete</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
